import { authApi } from '../services/api';
import {
  AUTH_CHECK_TIMEOUT_MS,
  AUTH_CHECK_RETRY_ATTEMPTS,
  AUTH_CHECK_RETRY_DELAY_MS,
  SessionTimeoutReason
} from '../constants/auth';

/**
 * Result of authentication check with retry logic
 */
export interface AuthCheckResult {
  success: boolean;
  userData?: unknown;
  reason?: SessionTimeoutReason;
}

/**
 * Delay helper for retry logic
 */
const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Performs authentication check with timeout and retry logic
 *
 * @returns AuthCheckResult with success status and optional user data
 */
export async function checkAuthenticationWithRetry(): Promise<AuthCheckResult> {
  let attempts = 0;
  const maxAttempts = AUTH_CHECK_RETRY_ATTEMPTS + 1; // Initial attempt + retries

  while (attempts < maxAttempts) {
    try {
      // Create timeout promise
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(
          () => reject(new Error('Authentication check timeout')),
          AUTH_CHECK_TIMEOUT_MS
        )
      );

      // Race between API call and timeout
      const userData = await Promise.race([
        authApi.getCurrentUser(),
        timeoutPromise
      ]);

      // Success!
      return {
        success: true,
        userData
      };
    } catch (error) {
      attempts++;

      // If this was the last attempt, return failure
      if (attempts >= maxAttempts) {
        // Determine reason for failure
        const errorMessage = error instanceof Error ? error.message : String(error);
        let reason: SessionTimeoutReason;

        if (errorMessage.includes('timeout')) {
          reason = SessionTimeoutReason.TIMEOUT;
        } else if (errorMessage.includes('401') || errorMessage.includes('Unauthorized')) {
          reason = SessionTimeoutReason.UNAUTHORIZED;
        } else {
          reason = SessionTimeoutReason.NETWORK_ERROR;
        }

        return {
          success: false,
          reason
        };
      }

      // Wait before retrying
      await delay(AUTH_CHECK_RETRY_DELAY_MS);
    }
  }

  // Fallback (should never reach here)
  return {
    success: false,
    reason: SessionTimeoutReason.NETWORK_ERROR
  };
}

/**
 * Builds login redirect URL with timeout reason for user-facing message
 *
 * @param redirectPath - Path to redirect to after login
 * @param reason - Reason for timeout (optional)
 * @returns Login URL with redirect and reason query parameters
 */
export function buildLoginRedirectUrl(
  redirectPath: string,
  reason?: SessionTimeoutReason
): string {
  const params = new URLSearchParams();
  params.set('redirect', redirectPath);

  if (reason) {
    params.set('reason', reason);
  }

  return `/login?${params.toString()}`;
}

/**
 * Builds admin login redirect URL with timeout reason
 *
 * @param redirectPath - Path to redirect to after login
 * @param reason - Reason for timeout (optional)
 * @returns Admin login URL with redirect and reason query parameters
 */
export function buildAdminLoginRedirectUrl(
  redirectPath: string,
  reason?: SessionTimeoutReason
): string {
  // Don't add redirect param if already on admin login page
  if (redirectPath === '/admin/login') {
    return '/admin/login';
  }

  const params = new URLSearchParams();
  params.set('redirect', redirectPath);

  if (reason) {
    params.set('reason', reason);
  }

  return `/admin/login?${params.toString()}`;
}
